import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCurrentOrgId } from '../auth';
import { queryConfigs } from '@/lib/queryClient';

interface LossReason {
  id: string;
  org_id: string;
  reason_text: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useLossReasons = (activeOnly = false) => {
  const { orgId } = useCurrentOrgId();

  return useQuery({
    ...queryConfigs.structural,
    queryKey: ["loss-reasons", orgId, activeOnly],
    queryFn: async () => {
      let query = supabase
        .from("loss_reasons")
        .select("*")
        .eq("org_id", orgId)
        .order("reason_text");

      if (activeOnly) {
        query = query.eq("is_active", true);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as LossReason[];
    },
    enabled: !!orgId,
  });
};

export const useCreateLossReason = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { orgId } = useCurrentOrgId();

  return useMutation({
    mutationFn: async (reason_text: string) => {
      const { data, error } = await supabase
        .from("loss_reasons")
        .insert({ org_id: orgId, reason_text, is_active: true })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["loss-reasons"] });
      queryClient.refetchQueries({ queryKey: ["loss-reasons"] });
      toast({
        title: "Sucesso",
        description: "Motivo de perda criado com sucesso.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erro",
        description: `Erro ao criar motivo de perda: ${error.message}`,
      });
    },
  });
};

export const useUpdateLossReason = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason_text }: { id: string; reason_text: string }) => {
      const { data, error } = await supabase
        .from("loss_reasons")
        .update({ reason_text })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["loss-reasons"] });
      queryClient.refetchQueries({ queryKey: ["loss-reasons"] });
      toast({
        title: "Sucesso",
        description: "Motivo de perda atualizado com sucesso.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erro",
        description: `Erro ao atualizar motivo de perda: ${error.message}`,
      });
    },
  });
};

export const useToggleLossReason = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { data, error } = await supabase
        .from("loss_reasons")
        .update({ is_active })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["loss-reasons"] });
      queryClient.refetchQueries({ queryKey: ["loss-reasons"] });
      toast({
        title: "Sucesso",
        description: "Status do motivo de perda atualizado com sucesso.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erro",
        description: `Erro ao atualizar status do motivo de perda: ${error.message}`,
      });
    },
  });
};
